import React, { useEffect, useState } from "react";
import axios from "../../api/axios";
import "./styles/AdminMessages.css";

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState("");
  const [expandedRows, setExpandedRows] = useState([]); // ✅ NEW — same as AdminOrders

  // Extract CSRF token
  useEffect(() => {
    const token = document.cookie
      .split("; ")
      .find((row) => row.startsWith("csrfToken="))
      ?.split("=")[1];
    if (token) setCsrfToken(token);
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get("/api/admin/messages");
      setMessages(response.data.data);
    } catch (error) {
      console.error("🔥 Error fetching messages:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  // Toggle drawer logic (same as AdminOrders)
  const toggleRow = (id) => {
    setExpandedRows((prev) =>
      prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
    );
  };

  const handleArchiveMessage = async (id, archived) => {
    if (!csrfToken) return console.error("🚨 Missing CSRF token");

    try {
      await axios.put(
        `/api/admin/messages/${id}/archive`,
        { archived: !archived },
        {
          withCredentials: true,
          headers: { "x-csrf-token": csrfToken },
        }
      );

      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === id ? { ...msg, archived: !archived } : msg
        )
      );
    } catch (error) {
      console.error("🔥 Error archiving message:", error);
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm("Delete this message permanently?")) return;
    if (!csrfToken) return console.error("🚨 Missing CSRF token");

    try {
      await axios.delete(`/api/admin/messages/${id}`, {
        withCredentials: true,
        headers: { "x-csrf-token": csrfToken },
      });

      fetchMessages();
    } catch (error) {
      console.error("🔥 Error deleting message:", error);
    }
  };

  const renderTable = (messageList, title) => (
    <div className="table-section">
      <h3 className="table-section-title">{title}</h3>

      <div className="table-wrapper">
        <table className="messages-table">
          <thead>
            <tr>
              <th>More</th>
              <th>Name</th>
              <th>Contact</th>
              <th>Message Preview</th>
              <th>Submitted</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {messageList.map((msg) => (
              <React.Fragment key={msg.id}>
                {/* ======================
                    MAIN ROW
                ======================= */}
                <tr>
                  <td>
                    <button
                      className={`expand-btn ${
                        expandedRows.includes(msg.id) ? "expanded" : ""
                      }`}
                      onClick={() => toggleRow(msg.id)}
                    >
                      <p>⬇️</p>
                    </button>
                  </td>

                  <td>{msg.name}</td>
                  <td>{msg.contact}</td>
                  <td className="message-preview">
                    {msg.message.length > 40
                      ? msg.message.slice(0, 40) + "..."
                      : msg.message}
                  </td>
                  <td>{new Date(msg.submitTime).toLocaleString()}</td>

                  <td className="table-actions">
                    <button
                      className="btn archive-btn"
                      onClick={() => handleArchiveMessage(msg.id, msg.archived)}
                    >
                      <i className="fa-solid fa-box-archive"></i>
                      {msg.archived ? " Unarchive" : " Archive"}
                    </button>

                    <button
                      className="btn delete-btn"
                      onClick={() => handleDeleteMessage(msg.id)}
                    >
                      <i className="fa-solid fa-xmark"></i> Delete
                    </button>
                  </td>
                </tr>

                {/* ======================
                    EXPANDED DRAWER ROW
                ======================= */}
                <tr className="order-details-row">
                  <td colSpan="6">
                    <div
                      className={`order-details-wrapper ${
                        expandedRows.includes(msg.id) ? "expanded" : ""
                      }`}
                    >
                      <div className="order-details">
                        <div className="order-shipping-info">
                          <h3>Message</h3>
                          <p className="full-message">{msg.message}</p>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (loading) return <p>Loading messages...</p>;

  const activeMessages = messages.filter((m) => !m.archived);
  const archivedMessages = messages.filter((m) => m.archived);

  return (
    <div className="admin-messages-container">
      <h2 className="messages-title">Contact Messages</h2>

      {activeMessages.length === 0 && archivedMessages.length === 0 ? (
        <p>No messages found.</p>
      ) : (
        <>
          {renderTable(activeMessages, "Active Messages")}
          {renderTable(archivedMessages, "Archived Messages")}
        </>
      )}
    </div>
  );
};

export default AdminMessages;
